import React, { useCallback, useMemo } from 'react';
import { AsoScenarioMinLayerType, EntityListTypeName } from '@ydistri/api-sdk';
import EntityListSelect from '../../../components/global/EntityListSelect';
import {
  useCreateAsoLayerMutation,
  useGetAsoLayersQuery,
  useUpdateAsoLayerMutation,
} from '../apiAso';
import { useAsoScenarioStatus } from '../hooks/useAsoScenarioStatus';

export interface AsoLayerDefinitionProps {
  asoScenarioId: number;
  layerTypeId: AsoScenarioMinLayerType; // Source | Target
  scopeId?: number; // optional
}

const types = [EntityListTypeName.MinLayer];

const AsoLayerDefinition: React.FC<AsoLayerDefinitionProps> = ({
  asoScenarioId,
  layerTypeId,
  scopeId,
}) => {
  const { data: layers } = useGetAsoLayersQuery(asoScenarioId);
  const [updateLayer] = useUpdateAsoLayerMutation();
  const [createLayer] = useCreateAsoLayerMutation();
  const { isLocked } = useAsoScenarioStatus();

  const matchingLayer = useMemo(() => {
    if (!layers) return undefined;

    return layers.find(
      l => l.asoScenarioMinLayerTypeId === layerTypeId && (l.scopeId ?? undefined) === scopeId,
    );
  }, [layers, layerTypeId, scopeId]);

  const value = matchingLayer?.listIds ?? [];

  const handleChange = useCallback(
    (listIds: number[]) => {
      if (isLocked) return;

      // Create layer on demand if it doesn't exist yet
      const layerId = matchingLayer?.id;
      if (!layerId) {
        createLayer({
          asoScenarioId,
          payload: { asoScenarioMinLayerTypeId: layerTypeId },
        }).then(({ data: created }) => {
          if (created) {
            updateLayer({
              asoScenarioId,
              layerId: created.id,
              payload: { scopeId, listIds },
            });
          }
        });
      } else {
        updateLayer({
          asoScenarioId,
          layerId: layerId,
          payload: { scopeId, listIds },
        });
      }
    },
    [asoScenarioId, scopeId, layerTypeId, matchingLayer, createLayer, updateLayer, isLocked],
  );

  const layersLoaded = layers !== undefined;
  const disabled = isLocked || !layersLoaded;

  return (
    <EntityListSelect
      multiselect={true}
      types={types}
      value={value}
      onChange={handleChange}
      disabled={disabled}
      loading={!layersLoaded}
      hideArchivedOptions={true}
    />
  );
};

export default AsoLayerDefinition;
